import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { StorageService } from '../../../core/services';
import { ImpersonationService } from '../../../domains/auth';
import { UserService } from '../../../domains/user';
import { SidebarItemDefinition } from '../sidebar-item/sidebar-item.model';
import { SidebarService } from '../sidebar.service';

interface RecentEntry {
    routerLink: string;
    lastVisitedUtc: string;
}

interface StoredShortcuts {
    pinnedRouterLinks: string[];
    recentEntries: RecentEntry[];
    isCollapsed: boolean;
}

export const MAX_PINNED_SHORTCUTS = 10;
const MAX_DISPLAYED_RECENTS = 5;
/* Retain more recents than displayed so unpinning can backfill the list. */
const MAX_RETAINED_RECENTS = 15;

/* All placeholders share /test, auth routes are wayfinding noise, and /home is the default
   redirect target (not a sidebar page) — never track them. */
const EXCLUDED_ROUTE_PREFIXES = ['/test', '/auth', '/home'];

const storageKeyPrefix = 'sidebar.shortcuts.';

/**
 * Pinned + recent sidebar shortcuts (DESIGN-SYSTEM.md §12.2). Stores routerLink strings only
 * and resolves them at render time against the permission-pruned sidebar library, so revoked
 * or renamed items self-heal. localStorage v1; pins move to SQL with the User migration —
 * see docs/sidebar-shortcuts-spec.md (the phase-2 reminder).
 */
@Injectable({
    providedIn: 'root'
})
export class ShortcutsService {
    private impersonationService = inject(ImpersonationService);
    private router = inject(Router);
    private sidebarService = inject(SidebarService);
    private storageService = inject(StorageService);
    private userService = inject(UserService);

    private _pinnedRouterLinks = signal<string[]>([]);
    private _recentEntries = signal<RecentEntry[]>([]);
    private _isCollapsed = signal(false);

    isCollapsed = this._isCollapsed.asReadonly();

    /* Impersonation renders the target's shortcuts read-only; recents also pause (see recordVisit). */
    canPin = computed(() => !this.impersonationService.isImpersonating() && !!this.userService.userContext());

    /* Cap counts RESOLVED pins so invisible (permission-revoked) pins can't soft-lock pinning. */
    isPinnedAtCap = computed(() => this.pinnedItems().length >= MAX_PINNED_SHORTCUTS);

    /** routerLink -> item map over the permission-pruned library, for render-time resolution. */
    private eligibleItemsByRouterLink = computed(() => {
        const itemsByRouterLink = new Map<string, SidebarItemDefinition>();
        const collectEligibleItems = (items: SidebarItemDefinition[]) => {
            for (const item of items) {
                if (this.isShortcutEligible(item)) {
                    itemsByRouterLink.set(item.routerLink!, item);
                }

                if (item.items?.length) {
                    collectEligibleItems(item.items);
                }
            }
        };

        collectEligibleItems(this.sidebarService.availableSidebarItems());
        return itemsByRouterLink;
    });

    /* Unresolvable links are hidden, not removed — a re-granted permission restores them. */
    pinnedItems = computed(() =>
        this._pinnedRouterLinks()
            .map((routerLink) => this.eligibleItemsByRouterLink().get(routerLink))
            .filter((item): item is SidebarItemDefinition => !!item)
    );

    recentItems = computed(() => {
        const pinnedRouterLinks = new Set(this._pinnedRouterLinks());

        return this._recentEntries()
            .filter((entry) => !pinnedRouterLinks.has(entry.routerLink))
            .map((entry) => this.eligibleItemsByRouterLink().get(entry.routerLink))
            .filter((item): item is SidebarItemDefinition => !!item)
            .slice(0, MAX_DISPLAYED_RECENTS);
    });

    hasShortcuts = computed(() => this.pinnedItems().length > 0 || this.recentItems().length > 0);

    constructor() {
        this.loadShortcuts();

        /* Reload per-user state whenever the effective user changes (sign-in, impersonation). */
        effect(() => {
            this.userService.userContext();
            this.loadShortcuts();
        });

        const subscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.recordVisit(event.urlAfterRedirects);
            }
        });
        inject(DestroyRef).onDestroy(() => subscription.unsubscribe());

        // The initial navigation may complete before this service is created.
        this.recordVisit(this.router.url);
    }

    isShortcutEligible(item: SidebarItemDefinition): boolean {
        return !!item.routerLink && !item.isPlaceholder && !item.url && !item.items?.length && !EXCLUDED_ROUTE_PREFIXES.some((prefix) => item.routerLink!.startsWith(prefix));
    }

    isPinned(routerLink: string): boolean {
        return this._pinnedRouterLinks().includes(routerLink);
    }

    /** Shared tooltip copy for every pin/unpin button (tree rows + shortcuts strip). */
    getPinActionTitle(isPinned: boolean): string {
        if (isPinned) {
            return 'Unpin shortcut';
        }

        return this.isPinnedAtCap() ? 'Pin limit reached' : 'Pin shortcut';
    }

    togglePinned(routerLink: string) {
        if (!this.canPin()) {
            return;
        }

        if (this.isPinned(routerLink)) {
            this._pinnedRouterLinks.update((pinned) => pinned.filter((pinnedLink) => pinnedLink !== routerLink));
        } else {
            if (this.isPinnedAtCap()) {
                return;
            }

            this._pinnedRouterLinks.update((pinned) => [...pinned, routerLink]);
        }

        this.saveShortcuts();
    }

    toggleCollapsed() {
        this._isCollapsed.update((isCollapsed) => !isCollapsed);
        this.saveShortcuts();
    }

    private recordVisit(url: string) {
        /* Recents pause during impersonation so the actor doesn't pollute the target's history. */
        if (this.impersonationService.isImpersonating() || !this.userService.userContext()) {
            return;
        }

        const path = url.split('?')[0].split('#')[0];

        /* Record optimistically: on a full page load NavigationEnd fires before the pruned
           sidebar library is built, so eligibility can't be checked here. Render-time
           resolution (recentItems) drops anything that never maps to a sidebar page. */
        if (path === '/' || EXCLUDED_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix))) {
            return;
        }

        this._recentEntries.update((entries) => [{ routerLink: path, lastVisitedUtc: new Date().toISOString() }, ...entries.filter((entry) => entry.routerLink !== path)].slice(0, MAX_RETAINED_RECENTS));

        this.saveShortcuts();
    }

    private getStorageKey(): string | null {
        const userId = this.userService.userContext()?.id;
        return userId ? `${storageKeyPrefix}${userId}` : null;
    }

    /* load/save are the seam phase 2 replaces with the SQL-backed API. */
    private loadShortcuts() {
        const storageKey = this.getStorageKey();
        const stored = storageKey ? this.storageService.getItem<Partial<StoredShortcuts>>(storageKey) : null;

        this._pinnedRouterLinks.set(Array.isArray(stored?.pinnedRouterLinks) ? stored.pinnedRouterLinks.filter((link) => typeof link === 'string') : []);
        this._recentEntries.set(Array.isArray(stored?.recentEntries) ? stored.recentEntries.filter((entry) => typeof entry?.routerLink === 'string') : []);
        this._isCollapsed.set(stored?.isCollapsed === true);
    }

    private saveShortcuts() {
        const storageKey = this.getStorageKey();

        if (!storageKey) {
            return;
        }

        const shortcuts: StoredShortcuts = {
            pinnedRouterLinks: this._pinnedRouterLinks(),
            recentEntries: this._recentEntries(),
            isCollapsed: this._isCollapsed()
        };
        this.storageService.setItem(storageKey, shortcuts);
    }
}
